"use client";

import React from "react";
import { Phone, Mail, ArrowRight, MapPin } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
export default function Finalcta() {
  const shouldReduceMotion = useReducedMotion();
  const nearbyAreas = [{
    name: "Bracknell",
    to: "/scaffolding-in-bracknell"
  }, {
    name: "Ascot",
    to: "/scaffolding-in-ascot"
  }];
  return <section id="finalcta" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="rounded-2xl bg-secondary text-secondary-foreground p-8 sm:p-12 lg:p-16 shadow-xl relative overflow-hidden">
          {/* Subtle decorative background accent */}
          <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-primary/10 pointer-events-none blur-3xl" />
          <div className="absolute -left-20 -bottom-20 w-80 h-80 rounded-full bg-primary/10 pointer-events-none blur-3xl" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Column: Heading and Contact Methods */}
            <div className="lg:col-span-7 space-y-6">
              <div className="space-y-3">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-secondary-foreground tracking-tight">
                  Request a scaffolding quote
                </h2>
                <p className="text-secondary-foreground/85 text-base sm:text-lg max-w-xl leading-relaxed">
                  Speak with our contracts team for project advice, site visits, and clear specifications. We cover Berkshire, Surrey, Hampshire, and the wider Home Counties.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <a href="tel:01344929900" className="flex items-center gap-3 p-4 rounded-xl bg-secondary-foreground/5 hover:bg-secondary-foreground/10 border border-secondary-foreground/10 transition-colors group">
                  <div className="size-11 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                    <Phone className="size-5" />
                  </div>
                  <div>
                    <span className="block text-xs uppercase tracking-wider text-secondary-foreground/70 font-medium">
                      Call our team
                    </span>
                    <span className="text-base font-semibold text-secondary-foreground group-hover:text-primary-foreground transition-colors">
                      {"01344 929900"}
                    </span>
                  </div>
                </a>

                <a href="mailto:quotes@bmfscaffolding.co.uk" className="flex items-center gap-3 p-4 rounded-xl bg-secondary-foreground/5 hover:bg-secondary-foreground/10 border border-secondary-foreground/10 transition-colors group">
                  <div className="size-11 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                    <Mail className="size-5" />
                  </div>
                  <div className="min-w-0">
                    <span className="block text-xs uppercase tracking-wider text-secondary-foreground/70 font-medium">
                      Email drawings or specs
                    </span>
                    <span className="text-base font-semibold text-secondary-foreground group-hover:text-primary-foreground transition-colors truncate block">
                      {"quotes@bmfscaffolding.co.uk"}
                    </span>
                  </div>
                </a>
              </div>
            </div>

            {/* Right Column: Main CTA Action */}
            <div className="lg:col-span-5 flex flex-col items-start lg:items-end justify-center">
              <div className="w-full sm:max-w-sm rounded-xl bg-card text-card-foreground p-6 sm:p-8 shadow-md border border-border space-y-5">
                <div className="space-y-1">
                  <h3 className="text-lg font-default font-semibold text-card-foreground">
                    Online quote form
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Provide project details and location for a prompt estimate.
                  </p>
                </div>

                <Button asChild size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-medium h-12 shadow-sm">
                  <Link to="/ContactRequestQuotePage" className="inline-flex items-center justify-center gap-2">
                    <span>Request a quote</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>

                <p className="text-xs text-muted-foreground text-center">
                  Safety-led scaffolding for domestic, commercial, and industrial contracts.
                </p>
              </div>
            </div>
          </div>

          <Separator className="my-8 bg-secondary-foreground/15" />

          {/* Nearby areas text links */}
          <div className="relative z-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-secondary-foreground/80">
            <div className="inline-flex items-center gap-2 font-medium text-secondary-foreground">
              <MapPin className="size-4 text-primary" />
              <span>Nearby areas:</span>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              {nearbyAreas.map((area, i) => <Link key={area.name} to={area.to} data-index={i} className="text-secondary-foreground/90 hover:text-primary-foreground underline underline-offset-4 decoration-secondary-foreground/30 hover:decoration-primary-foreground transition-colors font-medium">
                  {area.name}
                </Link>)}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}